import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, type UIMessage, type ChatOnDataCallback } from 'ai';
import { url } from 'constants/api.constants';
import { openErrorToaster } from 'services/toast.service';
import refreshTokenId from 'utils/firebase.util';
import { BAGEL_WORKFLOW_ID_STORAGE_KEY } from '../../api/bagel-workflow-session';
import { toBagelApiMessages } from '../lib/toBagelApiMessages';

type UseBagelChatStreamOptions = {
  onData?: ChatOnDataCallback<UIMessage>;
};

function getStoredWorkflowId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(BAGEL_WORKFLOW_ID_STORAGE_KEY);
}

export function createBagelChatTransport(pageContext?: string) {
  const baseUrl = url.API_URL?.replace(/\/$/, '') || '';

  return new DefaultChatTransport({
    api: `${baseUrl}/bagel/chat`,
    body: () => ({
      pageContext,
      workflowId: getStoredWorkflowId(),
    }),
    prepareSendMessagesRequest: ({ body, messages, ...rest }) => ({
      ...rest,
      body: {
        ...(typeof body === 'object' && body !== null ? body : {}),
        messages: toBagelApiMessages(messages),
        pageContext,
        workflowId: getStoredWorkflowId(),
      },
    }),
    fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
      const token = await refreshTokenId();
      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${token}`);
      const response = await fetch(input, { ...init, headers });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message =
          typeof errorBody.message === 'string'
            ? errorBody.message
            : `Chat request failed (${response.status})`;
        throw new Error(message);
      }

      return response;
    },
  });
}

export default function useBagelChatStream(
  pageContext?: string,
  options?: UseBagelChatStreamOptions,
) {
  return useChat({
    transport: createBagelChatTransport(pageContext),
    onData: options?.onData,
    onError: (err: Error) => {
      openErrorToaster(
        err.message || 'Bagel chat failed. Please try again.',
        5000,
      );
    },
  });
}
